import React, { useState } from 'react';
import { 
  Users, 
  GraduationCap, 
  Award, 
  Clock, 
  CheckCircle, 
  Star,
  ArrowRight,
  Target,
  BookOpen,
  UserCheck,
  Shield,
  Phone,
  Mail
} from 'lucide-react';

const SupervisionPage: React.FC = () => {
  const [selectedService, setSelectedService] = useState<string | null>(null);

  const supervisionServices = [
    {
      id: 'clinical-supervision',
      title: 'Mental Health Clinical Supervision',
      icon: <Users className="h-8 w-8 text-blue-600" />,
      description: 'Professional clinical supervision to help you better help your clients, patients, customers, stakeholders or audience.',
      targetAudience: [
        'Psychologists, Psychotherapists, Counsellors, other talking therapists',
        'Psychiatrists, Social Workers, Occupational Therapists, Support Workers',
        'Other allied health professionals',
        'Non-mental health workers looking for mental health supervision'
      ],
      features: [
        'Evidence-based supervision methods',
        'Tailored to your specific practice',
        'Flexible scheduling options',
        'Comprehensive case review',
        'Professional development focus'
      ]
    },
    {
      id: 'professional-mentorship',
      title: 'Professional Mentorship',
      icon: <GraduationCap className="h-8 w-8 text-green-600" />,
      description: 'Professional mentors to guide you on how to navigate your mental health career.',
      targetAudience: [
        'Early career mental health professionals',
        'Practitioners seeking career advancement',
        'Those transitioning between specialties',
        'Professionals seeking leadership development'
      ],
      features: [
        'Career navigation guidance',
        'Professional skills development',
        'Industry insights and networking',
        'Goal setting and achievement',
        'Character references (at mentor discretion)'
      ]
    },
    {
      id: 'accreditation-advisory',
      title: 'Professional Accreditation Advisory',
      icon: <Award className="h-8 w-8 text-purple-600" />,
      description: 'Professional accreditation advisory service to be registered with and accredited by a UK professional body.',
      targetAudience: [
        'Professionals seeking UK accreditation',
        'International practitioners moving to UK',
        'Those upgrading their credentials',
        'Organizations seeking compliance guidance'
      ],
      features: [
        'UK professional body guidance',
        'Application process support',
        'Documentation requirements',
        'Compliance strategies',
        'Ongoing maintenance advice'
      ]
    }
  ];

  const pricingOptions = [
    {
      option: 'Option 7',
      title: 'Professional Mentorship Individual Meeting',
      duration: '30 Minutes',
      format: 'Remote',
      price: 75,
      description: 'Individual mentorship session focused on career guidance and professional development.',
      includes: [
        'Career navigation discussion',
        'Professional guidance',
        'Goal setting support',
        'Character references (at discretion)'
      ],
      excludes: [
        'Report provision',
        'Form completion',
        'Document verification',
        'Professional reference letters/forms'
      ],
      note: 'Paperwork services should be arranged with a clinical supervisor, not the professional mentor.'
    },
    {
      option: 'Option 8',
      title: 'Accreditation Advisory Service',
      duration: '1 Hour',
      format: 'Remote',
      price: 'Contact for pricing',
      description: 'Comprehensive advisory service for registration and accreditation with UK professional bodies.',
      includes: [
        'UK professional body guidance',
        'Registration process walkthrough',
        'Requirements assessment',
        'Application strategy'
      ],
      excludes: [],
      note: 'Pricing varies based on specific accreditation requirements and complexity.'
    }
  ];

  const whoCanBenefit = [
    {
      category: 'Talking Therapy Professionals',
      professionals: [
        'Psychologists',
        'Psychotherapists', 
        'Counsellors',
        'Other talking therapists'
      ],
      icon: <UserCheck className="h-6 w-6 text-blue-500" />
    },
    {
      category: 'Medical & Allied Health',
      professionals: [
        'Psychiatrists',
        'Social Workers',
        'Occupational Therapists',
        'Support Workers',
        'Other allied health professionals'
      ],
      icon: <Shield className="h-6 w-6 text-green-500" />
    },
    {
      category: 'Non-Mental Health Workers',
      professionals: [
        'HR professionals',
        'Managers and supervisors',
        'Teachers and educators',
        'Healthcare workers from other specialties'
      ],
      icon: <Target className="h-6 w-6 text-purple-500" />
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Hero Section with Clinical Supervision Image */}
        <div className="bg-gradient-to-r from-blue-100 to-green-100 rounded-lg mb-12 overflow-hidden">
          <div className="flex flex-col lg:flex-row items-center">
            {/* Left Content */}
            <div className="lg:w-1/2 p-8 lg:p-12">
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Clinical Supervision & Professional Support
              </h1>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Professional supervision and mentorship services designed to enhance your clinical practice 
                and support your professional development in mental health care.
              </p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-yellow-500 fill-current" />
                  <span className="text-sm font-medium text-gray-700">Expert Supervision</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium text-gray-700">Professional Development</span>
                </div>
              </div>
            </div>
            
            {/* Right Content - Clinical Supervision Image */}
            <div className="lg:w-1/2 p-8">
              <div className="relative rounded-lg overflow-hidden shadow-xl">
                {/* Placeholder for the clinical supervision image */}
                <div className="aspect-[4/3] bg-gradient-to-br from-blue-200 to-purple-200 flex items-center justify-center">
                  <div className="text-center p-8">
                    <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Users className="h-12 w-12 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Clinical Supervision</h3>
                    <p className="text-sm text-gray-600">Professional support and guidance</p>
                  </div>
                </div>
                {/* In real implementation, this would be: */}
                {/* <img 
                  src={ClinicalSupervisionImage} 
                  alt="Clinical supervision session showing supportive hands clasped together"
                  className="w-full h-full object-cover"
                /> */}
              </div>
            </div>
          </div>
        </div>

        {/* Services Overview */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Our Supervision Services
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {supervisionServices.map((service) => (
              <div 
                key={service.id} 
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
                onClick={() => setSelectedService(selectedService === service.id ? null : service.id)}
              >
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    {service.icon}
                    <h3 className="text-xl font-bold text-gray-900">{service.title}</h3>
                  </div>
                  
                  <p className="text-gray-700 mb-6">{service.description}</p>
                  
                  {selectedService === service.id && (
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Target Audience:</h4>
                        <ul className="text-sm text-gray-700 space-y-1">
                          {service.targetAudience.map((audience, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                              {audience}
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Key Features:</h4>
                        <ul className="text-sm text-gray-700 space-y-1">
                          {service.features.map((feature, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <Star className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                  
                  <div className="mt-4 flex items-center text-blue-600 text-sm font-medium">
                    {selectedService === service.id ? 'Click to collapse' : 'Click to learn more'}
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Who Can Benefit */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Who Can Benefit from Our Services
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {whoCanBenefit.map((group, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center gap-3 mb-4">
                  {group.icon}
                  <h3 className="text-lg font-bold text-gray-900">{group.category}</h3>
                </div>
                
                <ul className="space-y-2">
                  {group.professionals.map((professional, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-700">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      {professional}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing Options */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Service Options & Pricing
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {pricingOptions.map((option, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-blue-100 rounded-full p-2">
                    <Clock className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{option.option}</h3>
                    <p className="text-gray-600">{option.title}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <p className="text-sm text-gray-600">Duration</p>
                    <p className="font-semibold">{option.duration}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Format</p>
                    <p className="font-semibold">{option.format}</p>
                  </div>
                </div>
                
                <div className="text-center mb-6">
                  <p className="text-3xl font-bold text-blue-600">
                    {typeof option.price === 'number' ? `£${option.price}.00` : option.price}
                  </p>
                </div>
                
                <p className="text-gray-700 mb-6">{option.description}</p>
                
                {option.includes.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-green-800 mb-2">✓ Includes:</h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      {option.includes.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {option.excludes.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-red-800 mb-2">✗ Excludes:</h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      {option.excludes.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <div className="h-4 w-4 border border-red-500 rounded-full mt-0.5 flex-shrink-0"></div>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {option.note && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> {option.note}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Ready to Start Your Professional Development Journey?
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Contact us to discuss your supervision, mentorship, or accreditation needs. 
              Our experienced team is ready to support your professional growth.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-blue-800 mb-4">Get in Touch</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Phone className="h-5 w-5 text-blue-600" />
                  <span className="text-blue-700">0333 800 3006</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-5 w-5 text-blue-600" />
                  <span className="text-blue-700">0333 789 0012</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-blue-600" />
                  <span className="text-blue-700">info@healthcity.uk</span>
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-green-800 mb-4">Quick Facts</h3>
              <ul className="space-y-2 text-green-700">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Flexible remote sessions available
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Experienced mental health professionals
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Tailored to your specific needs
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  UK professional body expertise
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupervisionPage;